/**
 * Authentication Service
 * Handles all authentication and signup-related API calls
 */

import {
  CompleteVerificationData,
  ForgotPasswordData,
  ResetPasswordData,
  User,
  UserClientRole,
  UserRole,
} from '@/types/user';
import { UserSignupData } from '@/types/user';
import { apiClient } from './apiClient';

type MeResponse = {
  user: User;
  authenticated: boolean;
  role: UserClientRole;
};

type LoginResponse = {
  user: User;
};

type VerificationResponse = {
  user: User;
};

type SendVerificationResponse = {
  email: string;
  user_role: UserRole;
};

type ValidateInvitationCodeResponse = {
  user: User;
};

type PassLivenessCheckResponse = {
  verification_url: string;
};

export const authService = {
  /**
   * Check current authentication status
   */
  async checkAuth() {
    return apiClient<MeResponse>('/api/me');
  },

  /**
   * Login user (unified endpoint for all user roles)
   */
  async login(email: string, password: string, role: UserClientRole) {
    return apiClient<LoginResponse>('/api/login', {
      method: 'POST',
      body: { email, password, user_role: role },
    });
  },

  /**
   * Logout user
   */
  async logout() {
    return apiClient<void>('/api/logout', {
      method: 'POST',
    });
  },

  /**
   * Send signup verification email (unified endpoint for all user roles)
   */
  async sendVerification(userData: UserSignupData) {
    return apiClient<SendVerificationResponse>('/api/signup/send-verification', {
      method: 'POST',
      body: userData,
    });
  },

  /**
   * Complete signup verification (unified endpoint for all user roles)
   */
  async completeVerification(data: CompleteVerificationData) {
    return apiClient<VerificationResponse>('/api/signup/complete-verification', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Validate qualification code
   */
  async validateInvitationCode(data: { invitation_code: string }) {
    return apiClient<ValidateInvitationCodeResponse>('/api/auth/invitation-code', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Get Veriff session
   */
  async passLivenessCheck() {
    return apiClient<PassLivenessCheckResponse>('/api/veriff/session', {
      method: 'GET',
    });
  },

  /**
   * Resend 2FA verification code
   */
  async resend2FACode(sessionId: string) {
    return apiClient<void>('/api/2fa/resend', {
      method: 'POST',
      body: { session_id: sessionId },
    });
  },

  /**
   * Request password reset
   */
  async forgotPassword(data: ForgotPasswordData) {
    return apiClient<void>('/api/auth/forgot-password', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Reset password
   */
  async resetPassword(data: ResetPasswordData) {
    return apiClient<void>('/api/auth/reset-password', {
      method: 'POST',
      body: data,
    });
  },
};
